import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const ServiceBreakdown = () => {
  const [searchParams] = useSearchParams();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const practiceArea = searchParams.get('area') || 'corporate-law';

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const serviceBreakdowns = {
    'corporate-law': {
      en: {
        title: 'Corporate Law Services',
        services: [
          {
            icon: 'Building',
            title: 'Business Formation & Structuring',
            description: 'Company incorporation, partnership agreements, joint ventures, and business restructuring to optimize operations and tax efficiency.'
          },
          {
            icon: 'FileText',
            title: 'Corporate Governance',
            description: 'Board governance, shareholder agreements, compliance frameworks, and corporate policy development for effective management.'
          },
          {
            icon: 'Handshake',
            title: 'Mergers & Acquisitions',
            description: 'Due diligence, transaction structuring, negotiation, and closing of mergers, acquisitions, and strategic partnerships.'
          },
          {
            icon: 'Shield',
            title: 'Regulatory Compliance',
            description: 'Industry-specific compliance, regulatory filings, licensing, and ongoing compliance monitoring and reporting.'
          },
          {
            icon: 'TrendingUp',
            title: 'Commercial Contracts',
            description: 'Contract drafting, negotiation, and review for supply agreements, distribution contracts, and service agreements.'
          },
          {
            icon: 'Users',
            title: 'Employment Law',
            description: 'Employment contracts, workplace policies, dispute resolution, and compliance with labor laws and regulations.'
          }
        ]
      },
      sw: {
        title: 'Huduma za Sheria ya Makampuni',
        services: [
          {
            icon: 'Building',
            title: 'Uundaji na Muundo wa Biashara',
            description: 'Uundaji wa kampuni, mikataba ya ushirikiano, miundano ya pamoja, na muundo upya wa biashara ili kuboresha utendaji na ufanisi wa kodi.'
          },
          {
            icon: 'FileText',
            title: 'Utawala wa Makampuni',
            description: 'Utawala wa bodi, mikataba ya wanahisa, mifumo ya kufuata sheria, na maendeleo ya sera za makampuni kwa usimamizi mzuri.'
          },
          {
            icon: 'Handshake',
            title: 'Kuunganisha na Ununuzi',
            description: 'Utafiti wa kina, muundo wa miamala, mazungumzo, na kufunga kuunganisha, ununuzi, na ushirikiano wa kimkakati.'
          },
          {
            icon: 'Shield',
            title: 'Kufuata Kanuni',
            description: 'Kufuata kanuni za sekta maalum, kufailia kanuni, leseni, na ufuatiliaji na ripoti za kufuata kanuni.'
          },
          {
            icon: 'TrendingUp',
            title: 'Mikataba ya Kibiashara',
            description: 'Kuandika mikataba, mazungumzo, na ukaguzi wa mikataba ya ugavi, mikataba ya usambazaji, na mikataba ya huduma.'
          },
          {
            icon: 'Users',
            title: 'Sheria ya Ajira',
            description: 'Mikataba ya ajira, sera za kazini, utatuzi wa migogoro, na kufuata sheria na kanuni za kazi.'
          }
        ]
      }
    },
    'family-law': {
      en: {
        title: 'Family Law Services',
        services: [
          {
            icon: 'Heart',
            title: 'Divorce & Separation',
            description: 'Contested and uncontested divorce proceedings, legal separation, and annulment with focus on fair resolution.'
          },
          {
            icon: 'Baby',
            title: 'Child Custody & Support',
            description: 'Child custody arrangements, visitation rights, child support calculations, and modification of existing orders.'
          },
          {
            icon: 'Home',
            title: 'Matrimonial Property',
            description: 'Division of marital assets, property valuation, spousal support, and protection of individual property rights.'
          },
          {
            icon: 'UserPlus',
            title: 'Adoption Services',
            description: 'Domestic and international adoption procedures, legal documentation, and court representation throughout the process.'
          },
          {
            icon: 'Scale',
            title: 'Domestic Violence Protection',
            description: 'Restraining orders, protection orders, and legal representation for domestic violence cases and family safety.'
          },
          {
            icon: 'FileHeart',
            title: 'Prenuptial Agreements',
            description: 'Pre-marital agreements, postnuptial agreements, and cohabitation agreements to protect individual interests.'
          }
        ]
      },
      sw: {
        title: 'Huduma za Sheria ya Familia',
        services: [
          {
            icon: 'Heart',
            title: 'Talaka na Kuachana',
            description: 'Michakato ya talaka yenye migogoro na isiyokuwa na migogoro, kuachana kisheria, na kubatilisha ndoa kwa kulenga utatuzi wa haki.'
          },
          {
            icon: 'Baby',
            title: 'Ulinzi wa Watoto na Msaada',
            description: 'Mipango ya ulinzi wa watoto, haki za kutembelea, mahesabu ya msaada wa watoto, na marekebisho ya amri zilizopo.'
          },
          {
            icon: 'Home',
            title: 'Mali za Ndoa',
            description: 'Mgawanyo wa mali za ndoa, uthamini wa mali, msaada wa mwenza, na ulinzi wa haki za mali za mtu binafsi.'
          },
          {
            icon: 'UserPlus',
            title: 'Huduma za Kupokea Watoto',
            description: 'Taratibu za kupokea watoto za ndani na kimataifa, nyaraka za kisheria, na uwakilishi wa mahakama katika mchakato wote.'
          },
          {
            icon: 'Scale',
            title: 'Ulinzi wa Unyanyasaji wa Nyumbani',
            description: 'Amri za kuzuia, amri za ulinzi, na uwakilishi wa kisheria kwa kesi za unyanyasaji wa nyumbani na usalama wa familia.'
          },
          {
            icon: 'FileHeart',
            title: 'Mikataba ya Kabla ya Ndoa',
            description: 'Mikataba ya kabla ya ndoa, mikataba ya baada ya ndoa, na mikataba ya kuishi pamoja kulinda maslahi ya mtu binafsi.'
          }
        ]
      }
    },
    'property-law': {
      en: {
        title: 'Property Law Services',
        services: [
          {
            icon: 'Home',
            title: 'Real Estate Transactions',
            description: 'Property purchases, sales, transfers, and conveyancing with comprehensive due diligence and documentation.'
          },
          {
            icon: 'Map',
            title: 'Land Registration & Titles',
            description: 'Title searches, land registration, title transfers, and resolution of title defects and boundary disputes.'
          },
          {
            icon: 'Building2',
            title: 'Property Development',
            description: 'Development agreements, planning permissions, construction contracts, and regulatory compliance for property projects.'
          },
          {
            icon: 'Scale',
            title: 'Property Disputes',
            description: 'Landlord-tenant disputes, boundary conflicts, easement issues, and property-related litigation representation.'
          },
          {
            icon: 'FileText',
            title: 'Lease Agreements',
            description: 'Commercial and residential lease drafting, negotiation, renewal, and dispute resolution for landlords and tenants.'
          },
          {
            icon: 'TrendingUp',
            title: 'Property Investment',
            description: 'Investment structuring, property financing, joint ventures, and legal advice for property investment portfolios.'
          }
        ]
      },
      sw: {
        title: 'Huduma za Sheria ya Mali',
        services: [
          {
            icon: 'Home',
            title: 'Miamala ya Mali',
            description: 'Ununuzi wa mali, mauzo, uhamisho, na uhamisho wa mali na utafiti wa kina na nyaraka kamili.'
          },
          {
            icon: 'Map',
            title: 'Usajili wa Ardhi na Hati',
            description: 'Utafutaji wa hati, usajili wa ardhi, uhamisho wa hati, na utatuzi wa kasoro za hati na migogoro ya mipaka.'
          },
          {
            icon: 'Building2',
            title: 'Maendeleo ya Mali',
            description: 'Mikataba ya maendeleo, ruhusa za mipango, mikataba ya ujenzi, na kufuata kanuni kwa miradi ya mali.'
          },
          {
            icon: 'Scale',
            title: 'Migogoro ya Mali',
            description: 'Migogoro ya mmiliki-mpangaji, migogoro ya mipaka, masuala ya njia za kupitia, na uwakilishi wa mashtaka yanayohusiana na mali.'
          },
          {
            icon: 'FileText',
            title: 'Mikataba ya Kukodisha',
            description: 'Kuandika mikataba ya kukodisha ya kibiashara na makazi, mazungumzo, upyaji, na utatuzi wa migogoro kwa wamiliki na wapangaji.'
          },
          {
            icon: 'TrendingUp',
            title: 'Uwekezaji wa Mali',
            description: 'Muundo wa uwekezaji, ufadhili wa mali, miundano ya pamoja, na ushauri wa kisheria kwa mfuko wa uwekezaji wa mali.'
          }
        ]
      }
    },
    'criminal-defense': {
      en: {
        title: 'Criminal Defense Services',
        services: [
          {
            icon: 'Shield',
            title: 'Criminal Defense Representation',
            description: 'Comprehensive defense for all criminal charges from minor offenses to serious felonies with aggressive advocacy.'
          },
          {
            icon: 'Search',
            title: 'Case Investigation',
            description: 'Thorough investigation of charges, evidence review, witness interviews, and building strong defense strategies.'
          },
          {
            icon: 'Scale',
            title: 'Court Representation',
            description: 'Skilled courtroom advocacy, plea negotiations, trial representation, and appellate proceedings when necessary.'
          },
          {
            icon: 'FileText',
            title: 'Legal Documentation',
            description: 'Preparation of legal motions, briefs, appeals, and all necessary documentation for criminal proceedings.'
          },
          {
            icon: 'Users',
            title: 'Client Consultation',
            description: 'Regular client communication, case updates, legal advice, and guidance throughout the criminal justice process.'
          },
          {
            icon: 'Clock',
            title: 'Emergency Services',
            description: 'Available for urgent matters, bail applications, emergency motions, and immediate legal assistance when needed.'
          }
        ]
      },
      sw: {
        title: 'Huduma za Utetezi wa Jinai',
        services: [
          {
            icon: 'Shield',
            title: 'Uwakilishi wa Utetezi wa Jinai',
            description: 'Utetezi kamili kwa mashtaka yote ya jinai kutoka makosa madogo hadi makosa makubwa na utetezi mkali.'
          },
          {
            icon: 'Search',
            title: 'Uchunguzi wa Kesi',
            description: 'Uchunguzi wa kina wa mashtaka, ukaguzi wa ushahidi, mahojiano ya mashahidi, na kujenga mikakati imara ya utetezi.'
          },
          {
            icon: 'Scale',
            title: 'Uwakilishi wa Mahakama',
            description: 'Utetezi wa ustadi wa chumba cha mahakama, mazungumzo ya kuomba, uwakilishi wa jaribio, na michakato ya rufaa inapohitajika.'
          },
          {
            icon: 'FileText',
            title: 'Nyaraka za Kisheria',
            description: 'Maandalizi ya hoja za kisheria, muhtasari, rufaa, na nyaraka zote zinazohitajika kwa michakato ya jinai.'
          },
          {
            icon: 'Users',
            title: 'Mazungumzo ya Mteja',
            description: 'Mawasiliano ya kawaida ya mteja, masasisho ya kesi, ushauri wa kisheria, na uongozi katika mchakato wa haki za jinai.'
          },
          {
            icon: 'Clock',
            title: 'Huduma za Dharura',
            description: 'Inapatikana kwa mambo ya haraka, maombi ya dhamana, hoja za dharura, na msaada wa haraka wa kisheria unapohitajika.'
          }
        ]
      }
    }
  };

  const currentBreakdown = serviceBreakdowns[practiceArea] || serviceBreakdowns['corporate-law'];
  const content = currentBreakdown[currentLanguage];

  return (
    <section className="py-16 bg-gradient-to-br from-surface/10 to-primary/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-4">
            {content.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {currentLanguage === 'en' ?'Comprehensive legal services tailored to meet your specific needs and objectives.' :'Huduma kamili za kisheria zilizobinafsishwa kukidhi mahitaji na malengo yako maalum.'
            }
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {content.services.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-8 shadow-md border border-border-light hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
                  <Icon name={service.icon} size={24} color="white" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-heading font-semibold text-text-primary mb-3">
                    {service.title}
                  </h3>
                  <p className="text-text-secondary leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceBreakdown;